/**
 * Route definitions with Lazy Loading enabled.
 * Components are grouped into logical chunks using webpackChunkName.
 */

export const routes = [
  // --- Public / Auth Routes ---
  {
    path: '/',
    name: 'nologin',
    component: () => import(/* webpackChunkName: "auth" */ '../components/NoLoginPage.vue')
  },
  {
    path: '/notauth',
    name: 'notauth',
    component: () => import(/* webpackChunkName: "auth" */ '../components/NoLoginPage.vue')
  },
  {
    path: '/login/:dealer',
    name: 'systemlogin',
    component: () => import(/* webpackChunkName: "auth" */ '../components/SystemLogin.vue')
  },
  {
    path: '/reset-password/:dealer',
    name: 'reset-password',
    component: () => import(/* webpackChunkName: "auth" */ '../components/ForgotPassword.vue'),
    meta: {auth: false}
  },
  {
    path: '/password/reset',
    name: 'reset-password-form',
    component: () => import(/* webpackChunkName: "auth" */ '../components/ResetPasswordForm.vue'),
    meta: {auth: false}
  },

  // --- Main Dashboard & Sub-routes ---
  {
    path: '/home',
    name: 'dashboardcaster',
    component: () => import(/* webpackChunkName: "main" */ '../components/DashBoardCaster.vue'),
    children: [
      // Home / Dashboard Views
      {
        path: '/sysadmin/network',
        name: 'casteradminhome',
        component: () => import(/* webpackChunkName: "admin" */ '../components/CasterAdminHome.vue')
      },
      {
        path: '/reseller/networkadmin',
        name: 'casterresellerhome',
        component: () => import(/* webpackChunkName: "reseller" */ '../components/CasterResellerHome.vue')
      },

      // Network & Reference Stations
      {
        path: '/network/loggedrovers',
        name: 'casterroverslogged',
        component: () => import(/* webpackChunkName: "network" */ '../components/CasterRoversLogged.vue')
      },
      {
        path: '/network/referencestations',
        name: 'casterreferencestations',
        component: () => import(/* webpackChunkName: "network" */ '../components/CasterReferenceStations.vue')
      },
      {
        path: '/network/referencestation/:id',
        name: 'casterreferencestation',
        component: () => import(/* webpackChunkName: "network" */ '../components/CasterReferenceStation.vue')
      },
      {
        path: '/network/display',
        name: 'casternetworkdisplay',
        component: () => import(/* webpackChunkName: "network" */ '../components/CasterNetworkDisplay.vue')
      },
      {
        path: '/manage/bases',
        name: 'casterbases',
        component: () => import(/* webpackChunkName: "network" */ '../components/CasterBases.vue')
      },

      // Management: Resellers, Dealers, Companies
      {
        path: '/manage/resellers',
        name: 'casterresellers',
        component: () => import(/* webpackChunkName: "manage" */ '../components/CasterResellers.vue')
      },
      {
        path: '/reseller/network/:id',
        name: 'casterreseller',
        component: () => import(/* webpackChunkName: "manage" */ '../components/CasterReseller.vue')
      },
      {
        path: '/manage/dealers',
        name: 'casterdealers',
        component: () => import(/* webpackChunkName: "manage" */ '../components/CasterDealers.vue')
      },
      {
        path: '/dealer/network/:id',
        name: 'casterdealer',
        component: () => import(/* webpackChunkName: "manage" */ '../components/CasterDealer.vue')
      },
      {
        path: '/manage/companies',
        name: 'castercompanies',
        component: () => import(/* webpackChunkName: "manage" */ '../components/CasterCompanies.vue')
      },
      {
        path: '/company/:id',
        name: 'castercompany',
        component: () => import(/* webpackChunkName: "manage" */ '../components/CasterCompany.vue')
      },

      // Management: Sim Cards & Orders
      {
        path: '/manage/sims',
        name: 'castersimms',
        component: () => import(/* webpackChunkName: "assets" */ '../components/CasterSimms.vue')
      },
      {
        path: '/manage/orders',
        name: 'casterorders',
        component: () => import(/* webpackChunkName: "assets" */ '../components/CasterOrders.vue')
      },

      // Subscriptions Management
      {
        path: '/subscriptionsstockdealer',
        name: 'castersubscriptionsstockdealer',
        component: () => import(/* webpackChunkName: "subs" */ '../components/CasterSubscriptionsStockDealer.vue')
      },
      {
        path: '/subscriptionsstockreseller',
        name: 'castersubscriptionsstockreseller',
        component: () => import(/* webpackChunkName: "subs" */ '../components/CasterSubscriptionsStockReseller.vue')
      },
      {
        path: '/subscriptionsstockreports',
        name: 'subscriptionsstockreports',
        component: () => import(/* webpackChunkName: "subs" */ '../components/CasterSubscriptionsStockReports.vue')
      },
      {
        path: '/manage/subscriptions',
        name: 'castersubscriptions',
        component: () => import(/* webpackChunkName: "subs" */ '../components/CasterSubscriptions.vue')
      },
      {
        path: '/manage/companysubscriptions',
        name: 'castercompanysubscriptions',
        component: () => import(/* webpackChunkName: "subs" */ '../components/CasterCompanySubscriptions.vue')
      },

      // Rover Management
      {
        path: '/manage/rovers',
        name: 'casterrovers',
        component: () => import(/* webpackChunkName: "rovers" */ '../components/CasterRovers.vue')
      },
      {
        path: '/manage/companyrovers',
        name: 'castercompanyrovers',
        component: () => import(/* webpackChunkName: "rovers" */ '../components/CasterCompanyRovers.vue')
      },
      {
        path: '/rover/:id',
        name: 'casterrovermanage',
        component: () => import(/* webpackChunkName: "rovers" */ '../components/CasterRoverManage.vue')
      },
      {
        path: '/rover/session/:id',
        name: 'casterrover',
        component: () => import(/* webpackChunkName: "rovers" */ '../components/CasterRover.vue')
      },
      {
        path: '/rover/sessionhistory/:id',
        name: 'casterroverhistory',
        component: () => import(/* webpackChunkName: "rovers" */ '../components/CasterRoverHistory.vue')
      },

      // User Management
      {
        path: '/manage/users',
        name: 'casterusers',
        component: () => import(/* webpackChunkName: "users" */ '../components/CasterUsers.vue')
      },
      {
        path: '/users/activity/:id',
        name: 'casteruseractivity',
        component: () => import(/* webpackChunkName: "users" */ '../components/CasterUserActivity.vue')
      },
      {
        path: '/systemadmin/users',
        name: 'casterusersadmin',
        component: () => import(/* webpackChunkName: "admin" */ '../components/CasterUsersAdmin.vue')
      },

      // Logs & Audits
      {
        path: '/logs/casterlogmessages',
        name: 'casterlogmessages',
        component: () => import(/* webpackChunkName: "logs" */ '../components/CasterLogMessages.vue')
      },
      {
        path: '/logs/casterloggga',
        name: 'casterloggga',
        component: () => import(/* webpackChunkName: "logs" */ '../components/CasterLogGGA.vue')
      },
      {
        path: '/logs/castereventlog',
        name: 'castereventlog',
        component: () => import(/* webpackChunkName: "logs" */ '../components/CasterEventLog.vue')
      },
      {
        path: '/logs/castereventlogdealer',
        name: 'castereventlogdealer',
        component: () => import(/* webpackChunkName: "logs" */ '../components/CasterEventLogDealer.vue')
      },
      {
        path: '/logs/castereventlogreseller',
        name: 'castereventlogreseller',
        component: () => import(/* webpackChunkName: "logs" */ '../components/CasterEventLogReseller.vue')
      },
      {
        path: '/logs/emails',
        name: 'casteremails',
        component: () => import(/* webpackChunkName: "logs" */ '../components/CasterEmails.vue')
      },
      {
        path: '/logs/gdpr',
        name: 'castergdpr',
        component: () => import(/* webpackChunkName: "logs" */ '../components/CasterGDPR.vue')
      },
      {
        path: '/systemadmin/audittrail',
        name: 'casteraudit',
        component: () => import(/* webpackChunkName: "admin" */ '../components/CasterAudit.vue')
      },

      // Statistics & Reports
      {
        path: '/stats/networklogins',
        name: 'casterstatsnetworklogins',
        component: () => import(/* webpackChunkName: "stats" */ '../components/CasterStatsNetworkLogins.vue')
      },
      {
        path: '/stats/ntripclients',
        name: 'casterstatsntripclients',
        component: () => import(/* webpackChunkName: "stats" */ '../components/CasterStatsNTRIPClients.vue')
      },
      {
        path: '/stats/casterstatssubs',
        name: 'casterstatssubs',
        component: () => import(/* webpackChunkName: "stats" */ '../components/CasterStatsSubs.vue')
      },
      {
        path: '/systemadmin/reports',
        name: 'casterreports',
        component: () => import(/* webpackChunkName: "admin" */ '../components/CasterReports.vue')
      },

      // System Admin Extras
      {
        path: '/systemadmin/emailtemplates',
        name: 'casteremailtemplates',
        component: () => import(/* webpackChunkName: "admin" */ '../components/CasterEmailTemplates.vue')
      },
      {
        path: '/systemadmin/systemsettings',
        name: 'castersystemsettings',
        component: () => import(/* webpackChunkName: "admin" */ '../components/CasterSystemSettings.vue')
      },
      {
        path: '/system/status',
        name: 'castersystemstatus',
        component: () => import(/* webpackChunkName: "admin" */ '../components/CasterSystemStatus.vue')
      }
    ]
  },
  // ERROR pages
  {
    path: '/401',
    name: 'unauthenticated',
    component: () => import(/* webpackChunkName: "error" */ '../components/Error401.vue')
  },
  {
    path: '/403',
    name: 'forbidden',
    component: () => import(/* webpackChunkName: "error" */ '../components/Error403.vue')
  },
  {
    path: '/404',
    name: 'notfound',
    component: () => import(/* webpackChunkName: "error" */ '../components/Error404.vue')
  },
  {
    path: '*',
    redirect: { name: 'notfound' }
  },
]
